/**
 * ProfileList — directory of Profiles in the tenant.
 *
 * Per /shared/profile_schema.md visibility table, the list view shows
 * identity-level fields. Behavioral content + notes are gated to the
 * detail view + tier permissions.
 *
 * Manager Handoff (#75 follow-up / Item 18): each row carries a Reassign
 * action for the Profile's owner or a workspace admin. An `?owner=<user_id>`
 * query param filters the list to one teammate's Profiles — the offboarding
 * path the Team-tab "remove blocked" notice deep-links into.
 */

import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import { SectionHeader, Callout, Chip } from "../components/index.js";
import PredispositionProfileHelpTooltip from "../components/PredispositionProfileHelpTooltip.jsx";
import ReassignProfileModal from "../components/ReassignProfileModal.jsx";

export default function ProfileListPage() {
  const { session, appUser, loading } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerFilter = searchParams.get("owner") || "";

  const [profiles, setProfiles] = useState([]);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!session) return;
    const ctrl = new AbortController();
    setFetching(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (modeFilter) params.set("mode", modeFilter);
    if (ownerFilter) params.set("owner", ownerFilter);
    fetch(`/.netlify/functions/profiles?${params.toString()}`, {
      headers: { authorization: `Bearer ${session.access_token}` },
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((b) => { setProfiles(b.profiles || []); setError(null); })
      .catch((e) => { if (e.name !== "AbortError") setError(e.message); })
      .finally(() => setFetching(false));
    return () => ctrl.abort();
  }, [session, search, modeFilter, ownerFilter, reloadTick]);

  if (loading) return <p>Loading…</p>;
  if (!session) return <p>Sign in to view Profiles.</p>;

  const isAdmin = !!appUser?.is_tenant_admin;
  const canReassign = (p) => isAdmin || p.owner_user_id === appUser?.user_id;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px" }}>
      <SectionHeader title="People" />

      {/*
        First-visit help tooltip — V3 Decision Science noun rollout (Phase
        1.7, 2026-05-19). Surfaces "Predisposition Profile" formally on the
        list view so users learn the canonical term once; dismissable;
        persisted via localStorage key pp.help.profile-noun.dismissed.v1.
        Reference: /design/mockups/Phase3_Profile_ListView.html.
      */}
      <PredispositionProfileHelpTooltip />

      {ownerFilter && (
        <div style={ownerBannerStyle}>
          <span>
            Showing one owner's Profiles — the handoff view.
          </span>
          <button
            type="button"
            style={ownerBannerClearStyle}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("owner");
              setSearchParams(next);
            }}
          >
            Clear filter
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email…"
          style={{
            flex: "1 1 280px",
            padding: "12px 14px",
            fontFamily: "var(--ppr-typography-font-family-ui)",
            fontSize: "var(--ppr-typography-font-size-base)",
            border: "1px solid var(--ppr-color-border-default)",
            borderRadius: "var(--ppr-radius-sm)",
            background: "var(--ppr-color-background-surface)",
            color: "var(--ppr-color-text-primary)",
          }}
        />
        <Link
          to="/profiles/new"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "center",
            lineHeight: 1,
            fontFamily: "var(--ppr-typography-font-family-ui)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "0.04em",
            padding: "11px 16px",
            borderRadius: "var(--ppr-radius-sm)",
            border: "1px solid var(--ppr-color-accent-navy)",
            background: "var(--ppr-color-accent-navy)",
            color: "var(--ppr-color-text-inverse)",
            textDecoration: "none",
          }}
        >
          + New person
        </Link>
      </div>

      {error && <ErrorState error={error} />}

      {!fetching && profiles.length === 0 && (
        <Callout.Tip>
          No profiles match yet. Try a different search, or add the first Profile via "+ New Profile".
        </Callout.Tip>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {profiles.map((p) => (
          <ProfileRow
            key={p.internal_uuid}
            profile={p}
            canReassign={canReassign(p)}
            onReassign={() => setReassignTarget(p)}
          />
        ))}
      </div>

      {reassignTarget && (
        <ReassignProfileModal
          profile={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onReassigned={() => setReloadTick((t) => t + 1)}
        />
      )}
    </div>
  );
}

function ProfileRow({ profile, canReassign, onReassign }) {
  return (
    <div style={rowWrapStyle}>
      <Link
        to={`/profiles/${profile.internal_uuid}`}
        style={rowLinkStyle}
      >
        <div>
          <div style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: "16px", color: "var(--ppr-color-text-primary)" }}>
            {profile.display_name || "—"}
          </div>
        </div>
        <div style={{ fontSize: "var(--ppr-typography-font-size-sm)", color: "var(--ppr-color-text-secondary)" }}>
          {profile.role_title || ""} {profile.department ? `· ${profile.department}` : ""}
        </div>
        <div style={{ fontSize: "var(--ppr-typography-font-size-sm)", color: "var(--ppr-color-text-secondary)" }}>
          {profile.work_email || "no email"}
        </div>
        <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "11px", color: "var(--ppr-color-text-muted)", textAlign: "right" }}>
          {profile.updated_at ? new Date(profile.updated_at).toLocaleDateString() : ""}
        </div>
      </Link>
      <div style={rowActionCellStyle}>
        {canReassign && (
          <button type="button" onClick={onReassign} style={reassignBtnStyle}>
            Reassign
          </button>
        )}
      </div>
    </div>
  );
}

function ModePill({ mode }) {
  const styles = {
    Lite:      { bg: "var(--ppr-color-background-muted)",     fg: "var(--ppr-color-text-muted)" },
    Full:      { bg: "var(--ppr-color-accent-pale-soft)",     fg: "var(--ppr-color-accent-navy)" },
    Departed:  { bg: "var(--ppr-color-background-muted)",     fg: "var(--ppr-color-text-secondary)" },
    LegalHold: { bg: "var(--ppr-color-accent-coral-very-soft)", fg: "var(--ppr-color-accent-coral-dark)" },
  }[mode] || { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-muted)" };
  return (
    <span style={{
      padding: "4px 10px",
      background: styles.bg,
      color: styles.fg,
      fontFamily: "var(--ppr-typography-font-family-display)",
      fontSize: "11px",
      letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
      textTransform: "uppercase",
      textAlign: "center",
    }}>
      {mode}
    </span>
  );
}

function ErrorState({ error }) {
  return (
    <div style={{
      padding: "12px 16px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      marginBottom: 16,
    }}>
      <div style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: "13px", textTransform: "uppercase", color: "var(--ppr-color-accent-coral-dark)" }}>
        Error
      </div>
      <div>{error}</div>
    </div>
  );
}

const rowWrapStyle = {
  display: "flex",
  alignItems: "center",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  background: "var(--ppr-color-background-surface)",
};

const rowLinkStyle = {
  flex: "1 1 auto",
  minWidth: 0,
  display: "grid",
  gridTemplateColumns: "1.4fr 2fr 1.3fr 0.7fr",
  gap: 16,
  padding: "14px 18px",
  textDecoration: "none",
  color: "var(--ppr-color-text-primary)",
  alignItems: "center",
};

const rowActionCellStyle = {
  flex: "0 0 auto",
  padding: "0 14px",
  borderLeft: "1px solid var(--ppr-color-border-default)",
  alignSelf: "stretch",
  display: "flex",
  alignItems: "center",
};

const reassignBtnStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "12.5px",
  fontWeight: 600,
  color: "var(--ppr-color-accent-steel)",
  background: "none",
  border: "none",
  padding: "6px 4px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const ownerBannerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
  padding: "10px 14px",
  marginBottom: 16,
  borderLeft: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-pale-soft)",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  color: "var(--ppr-color-text-primary)",
};

const ownerBannerClearStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "12.5px",
  fontWeight: 600,
  color: "var(--ppr-color-accent-navy)",
  background: "none",
  border: "none",
  cursor: "pointer",
  textDecoration: "underline",
};
